/**
 * API functions for order image management
 */

import { getSessionUUID } from '../components/lib/AuthToken';
import logger from '../utils/logger';

// Upload an image for an order
export const uploadOrderImage = async (orderId, imageFile) => {
    try {
        const sessionToken = await getSessionUUID();
        
        const formData = new FormData();
        formData.append('image', imageFile);
        
        const requestOptions = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${sessionToken}`
            },
            body: formData
        };

        const response = await fetch(`/order_images/upload_order_image/${orderId}`, requestOptions);
        
        // Read response as text first, then try to parse as JSON
        const responseText = await response.text();
        
        let responseBody;
        try {
            responseBody = JSON.parse(responseText);
        } catch (jsonError) {
            // If JSON parsing fails, the response might be HTML or plain text
            logger.error("JSON parse error, response was:", responseText);
            return {
                message: `Server error: ${response.status} - ${response.statusText}`,
                status: response.status,
                data: null,
                success: false,
            };
        }

        return {
            message: responseBody.message || responseBody.error || 'Image upload completed',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error uploading order image:", err);
        return {
            message: `Network error: ${err.message}`,
            status: 0,
            data: null,
            success: false,
        };
    }
};

// Get the public URL for an order's image
export const getOrderImageUrl = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID();
        
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/order_images/get_order_image_url/${orderId}`, requestOptions);
        
        // Read response as text first, then try to parse as JSON
        const responseText = await response.text();
        
        let responseBody;
        try {
            responseBody = JSON.parse(responseText);
        } catch (jsonError) {
            logger.error("JSON parse error, response was:", responseText);
            return {
                message: `Server error: ${response.status} - ${response.statusText}`,
                status: response.status,
                data: null,
                success: false,
            };
        }

        return {
            message: responseBody.message || responseBody.error || 'Image URL retrieved',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error getting order image URL:", err);
        return {
            message: `Network error: ${err.message}`,
            status: 0,
            data: null,
            success: false,
        };
    }
};

// Delete an order's image
export const deleteOrderImage = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID();
        
        const requestOptions = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/order_images/delete_order_image/${orderId}`, requestOptions);
        
        // Read response as text first, then try to parse as JSON
        const responseText = await response.text();
        
        let responseBody;
        try {
            responseBody = JSON.parse(responseText);
        } catch (jsonError) {
            logger.error("JSON parse error, response was:", responseText);
            return {
                message: `Server error: ${response.status} - ${response.statusText}`,
                status: response.status,
                data: null,
                success: false,
            };
        }

        return {
            message: responseBody.message || responseBody.error || 'Image deleted',
            status: response.status,
            data: responseBody || null,
            success: response.ok,
        };
    } catch (err) {
        logger.error("Error deleting order image:", err);
        return {
            message: `Network error: ${err.message}`,
            status: 0,
            data: null,
            success: false,
        };
    }
};